import { AuthUser } from '../types/auth';
import {
  applyActionCode,
  Auth,
  checkActionCode,
  confirmPasswordReset,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
} from 'firebase/auth';
import { getModuleDictionary } from '@/shared/i18n';
import { z } from 'zod';
import { TaskEither, Either, left, right, taskEither } from '@shared-commons/utils';

const loginSchema = z.object({
  email: z.string().email({ message: "L'indirizzo email non è valido." }),
  password: z.string().min(8, { message: 'La password deve avere almeno 8 caratteri.' }),
});

export const authenticateUser = (
  email: string,
  password: string,
  auth: Auth,
): TaskEither<Error, AuthUser> => {
  const task = async (): Promise<Either<Error, AuthUser>> => {
    const dict = await getModuleDictionary('it', 'auth');

    // Validazione con Zod
    const parseResult = loginSchema.safeParse({ email, password });
    if (!parseResult.success) {
      const errorMessage = parseResult.error.errors[0]?.message || 'Errore di validazione';
      console.log('Errore durante la validazione:', errorMessage);
      return left(new Error(errorMessage));
    }

    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;

      const token = await user.getIdToken();

      if (!user.emailVerified) {
        const errorMessage = 'Utente non verificato';
        console.log(`Utente non verificato:`, errorMessage);

        return left(new Error(errorMessage));
      }
      const authUser: AuthUser = {
        uid: user.uid,
        email: user.email || '',
        token,
        refreshToken: user.refreshToken, // <-- Salvataggio del refreshToken
        tokenCreationDate: new Date().toISOString(),
      };

      // await sendSsoCredentials(user.email || '', user.uid, token)();

      return right(authUser);
    } catch (error) {
      const errorMessageRaw = (error as Error).message;

      console.log('Errore grezzo ricevuto:', errorMessageRaw);

      const firebaseErrorKey = Object.keys(dict.auth_errors || {}).find((key) =>
        errorMessageRaw.includes(key),
      );

      console.log('Chiave Firebase trovata:', firebaseErrorKey);
      console.log('Dizionario auth_errors:', dict.auth_errors);

      const errorMessage =
        (firebaseErrorKey && dict.auth_errors?.[firebaseErrorKey]) ||
        dict.auth_errors?.generic_error ||
        'Si è verificato un errore. Riprova più tardi.';

      console.log(`Messaggio d'errore finale:`, errorMessage);

      return left(new Error(errorMessage));
    }
  };
  return taskEither(task);
};

export const resendRegistrationEmail = (
  email: string,
  name: string,
): TaskEither<Error, boolean> => {
  const task = async (): Promise<Either<Error, boolean>> => {
    const dict = await getModuleDictionary('it', 'auth');
    try {
      console.log(`Resending Email to user: ${email}, name: ${name}`);
      const response = await fetch(
        `${process.env.NEXT_PUBLIC_HOST}/api/register?email=${encodeURIComponent(email || email)}&name=${encodeURIComponent(name)}`,
        {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' },
        },
      );

      if (!response.ok || response.status >= 400) {
        const errorResponse = await response.json();
        const errorMessage =
          dict.auth_errors?.[errorResponse.code] ||
          dict.auth_errors?.generic_error ||
          errorResponse.message ||
          "Errore durante la chiamata all'API di registrazione";
        console.log('Errore originale:', (errorResponse as Error).message);
        console.log('Errore tradotto:', errorMessage);
        return left(new Error(errorMessage));
      }
      console.log(`User successfully resend registration confirmation email`);
      return right(true);
    } catch (error) {
      const firebaseErrorCode = (error as any).code;
      const errorMessage =
        dict.auth_errors?.[firebaseErrorCode] ||
        dict.auth_errors?.generic_error ||
        (error as Error).message ||
        "Errore durange l'invio dell'email di conferma";
      console.log('Errore originale:', (error as Error).message);
      console.log('Errore tradotto:', errorMessage);
      return left(new Error(errorMessage));
    }
  };
  return taskEither(task);
};

export const registerUser = (
  email: string,
  password: string,
  name: string,
  auth: Auth,
): TaskEither<Error, boolean> => {
  const task = async (): Promise<Either<Error, boolean>> => {
    const dict = await getModuleDictionary('it', 'auth');
    try {
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;
      console.log(`User created with Firebase UID: ${user.uid}, Email: ${user.email}`);

      const response = await fetch(
        `${process.env.NEXT_PUBLIC_HOST}/api/register?email=${encodeURIComponent(user.email || email)}&name=${encodeURIComponent(name)}`,
        {
          method: 'GET',
        },
      );

      if (!response.ok || response.status >= 400) {
        const errorResponse = await response.json();
        const errorMessage =
          dict.auth_errors?.[errorResponse.code] ||
          dict.auth_errors?.generic_error ||
          errorResponse.message ||
          "Errore durante la chiamata all'API di registrazione";
        console.log('Errore originale:', (errorResponse as Error).message);
        console.log('Errore tradotto:', errorMessage);
        return left(new Error(errorMessage));
      }
      console.log(`User successfully registered in backend: ${user.uid}`);
      return right(true);
    } catch (error) {
      const firebaseErrorCode = (error as any).code;
      const errorMessage =
        dict.auth_errors?.[firebaseErrorCode] ||
        dict.auth_errors?.generic_error ||
        (error as Error).message ||
        'Errore durante la registrazione.';
      console.log('Errore originale:', (error as Error).message);
      console.log('Errore tradotto:', errorMessage);
      return left(new Error(errorMessage));
    }
  };
  return taskEither(task);
};

export const getTokenService = (
  user: AuthUser | null,
  setUser: (user: AuthUser) => void,
  auth: Auth,
): TaskEither<Error, string> =>
  taskEither(async () => {
    if (!user) {
      return left(new Error('Utente non autenticato.'));
    }

    const { token, tokenCreationDate } = user;

    if (!token || !tokenCreationDate) {
      return left(new Error('Token mancante o non valido.'));
    }

    const tokenCreationTime = new Date(tokenCreationDate).getTime();
    const currentTime = Date.now();

    // Standard token expiration time - 55 minutes (tokens typically expire after 1 hour)
    const TOKEN_EXPIRATION_TIME = 55 * 60 * 1000;

    // Different refresh logic based on environment
    if (currentTime - tokenCreationTime > TOKEN_EXPIRATION_TIME) {
      // Regular browser environment - refresh token when approaching expiration
      try {
        console.log('Refreshing token in browser environment');
        if (!auth.currentUser) {
          console.warn('Auth currentUser is null during token refresh');
          return right(token); // Use existing token as fallback
        }

        const newToken = await auth.currentUser.getIdToken(true);
        if (!newToken) {
          console.warn('Failed to obtain new token, using existing one');
          return right(token); // Use existing token as fallback
        }

        setUser({
          ...user,
          token: newToken,
          tokenCreationDate: new Date().toISOString(),
        });

        return right(newToken);
      } catch (error: any) {
        console.error('Errore durante il refresh del token:', error);
        // In browser, still try to continue with existing token
        return right(token);
      }
    } else {
      // Token is still valid, use existing one
      return right(token);
    }
  });

export const activateUser = (activationCode: string, auth: Auth): TaskEither<Error, void> => {
  const task = async (): Promise<Either<Error, void>> => {
    const dict = await getModuleDictionary('it', 'auth');
    try {
      // Ottieni le informazioni associate al codice di azione
      const info = await checkActionCode(auth, activationCode);
      const email = info.data.email;

      // Verifica se l'email è disponibile
      if (!email) {
        return left(
          new Error(
            dict.auth_errors?.no_user ||
              'Errore: Nessun indirizzo email trovato o email non disponibile.',
          ),
        );
      }
      // Applica il codice di azione per verificare l'email
      await applyActionCode(auth, activationCode);

      // Prepara il corpo della richiesta con l'email ottenuta
      const body = JSON.stringify({
        email: email,
        name: 'Utente', // Puoi modificare questo valore se necessario
      });

      const response = await fetch(`${process.env.NEXT_PUBLIC_HOST}/api/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: body,
      });

      /*  const response = await fetch(
        `${process.env.NEXT_PUBLIC_HOST}/api/register?email=${encodeURIComponent(email)}&name=${encodeURIComponent('Utente')}`,
        {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' },
        },
      ); */

      if (!response.ok) {
        const errorResponse = await response.json();
        const errorMessage =
          dict.auth_errors?.[errorResponse.code] ||
          dict.auth_errors?.generic_error ||
          errorResponse.message ||
          "Errore durante la chiamata all'API di attivazione";
        console.error('Errore originale:', (errorResponse as Error).message);
        console.error('Errore tradotto:', errorMessage);
        return left(new Error(errorMessage));
      }
      return right(undefined);
    } catch (error) {
      const firebaseErrorCode = (error as any).code;
      const errorMessage =
        dict.auth_errors?.[firebaseErrorCode] ||
        dict.auth_errors?.generic_error ||
        (error as Error).message ||
        "Errore durante l'attivazione.";
      console.error('Errore originale:', (error as Error).message);
      console.error('Errore tradotto:', errorMessage);
      return left(new Error(errorMessage));
    }
  };
  return taskEither(task);
};

export const resetPassword = (
  oobCode: string,
  newPassword: string,
  auth: Auth,
): TaskEither<Error, void> => {
  const task = async (): Promise<Either<Error, void>> => {
    const dict = await getModuleDictionary('it', 'auth');

    const passwordSchema = z
      .string()
      .min(8, 'Must be at least 8 characters')
      .regex(/\d/, 'Must contain at least one number');
    const result = passwordSchema.safeParse(newPassword);

    if (!result.success) {
      return left(new Error(dict.auth_errors?.short_password));
    }
    try {
      await confirmPasswordReset(auth, oobCode, newPassword);
      return right(undefined);
    } catch (error) {
      const firebaseErrorCode = (error as any).code;
      const errorMessage =
        dict.auth_errors?.[firebaseErrorCode] ||
        dict.auth_errors?.generic_error ||
        (error as Error).message ||
        'Errore sconosciuto durante il reset della password.';
      console.error('Errore originale:', (error as Error).message);
      console.error('Errore tradotto:', errorMessage);
      return left(new Error(errorMessage));
    }
  };
  return taskEither(task);
};

export const forgotPassword = (email: string): TaskEither<Error, void> => {
  const task = async (): Promise<Either<Error, void>> => {
    const dict = await getModuleDictionary('it', 'auth');
    try {
      if (email) {
        const response = await fetch(`${process.env.NEXT_PUBLIC_HOST}/api/reset-password`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email }),
        });
        if (!response.ok) {
          const errorData = await response.json();
          const errorMessage =
            dict.auth_errors?.[errorData.code] ||
            dict.auth_errors?.generic_error ||
            (errorData as Error).message ||
            'Errore sconosciuto durante la richiesta.';
          console.error('Errore originale:', (errorData as Error).message);
          console.error('Errore tradotto:', errorMessage);
          return left(new Error(errorMessage));
        }
        return right(undefined);
      }
      return left(new Error(dict.auth_errors?.email_not_provided || 'Email non fornita.'));
    } catch (error) {
      const errorMessage =
        dict.auth_errors?.[(error as Error).message] ||
        dict.auth_errors?.generic_error ||
        (error as Error).message;
      console.error('Errore originale:', (error as Error).message);
      console.error('Errore tradotto:', errorMessage);
      return left(new Error(errorMessage));
    }
  };
  return taskEither(task);
};

export const updatePassword = (email: string): TaskEither<Error, { oobCode: string }> => {
  const task = async (): Promise<Either<Error, { oobCode: string }>> => {
    const dict = await getModuleDictionary('it', 'auth');
    try {
      if (email) {
        const response = await fetch(`${process.env.NEXT_PUBLIC_HOST}/api/update-password`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email }),
        });
        if (!response.ok) {
          const errorData = await response.json();
          const errorMessage =
            dict.auth_errors?.[errorData.code] ||
            dict.auth_errors?.generic_error ||
            (errorData as Error).message ||
            'Errore sconosciuto durante la richiesta.';
          console.error('Errore originale:', (errorData as Error).message);
          console.error('Errore tradotto:', errorMessage);
          return left(new Error(errorMessage));
        }
        console.log(response, 'response');
        const { oobCode } = await response.json();
        return right({ oobCode });
      }
      return left(new Error(dict.auth_errors?.email_not_provided || 'Email non fornita.'));
    } catch (error) {
      const errorMessage =
        dict.auth_errors?.[(error as Error).message] ||
        dict.auth_errors?.generic_error ||
        (error as Error).message;
      console.error('Errore originale:', (error as Error).message);
      console.error('Errore tradotto:', errorMessage);
      return left(new Error(errorMessage));
    }
  };
  return taskEither(task);
};

export const logoutUser = (): TaskEither<Error, void> => {
  const task = async (): Promise<Either<Error, void>> => {
    try {
      console.log('Logout avvenuto con successo');
      return right(undefined);
    } catch (error) {
      console.error('Errore durante il logout:', (error as Error).message);
      return left(new Error('Logout failed'));
    }
  };
  return taskEither(task);
};

/**
 * @deprecated
 * @returns
 */
export const completeRegistration = (auth: Auth): TaskEither<Error, { success: boolean }> => {
  const task = async (): Promise<Either<Error, { success: boolean }>> => {
    const dict = await getModuleDictionary('it', 'auth');
    try {
      const idToken = await auth.currentUser?.getIdToken();
      if (!idToken) {
        throw new Error(dict.auth_errors?.unauthenticated_user || 'Utente non autenticato.');
      }
      const response = await fetch(`${process.env.NEXT_PUBLIC_HOST}/api/complete-registration`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${idToken}`,
        },
      });
      if (response.ok) {
        return right({ success: true });
      } else {
        const data = await response.json();
        const errorMessage =
          dict.auth_errors?.[data.code] ||
          dict.auth_errors?.generic_error ||
          (data as Error).message ||
          'Errore durante il completamento della registrazione.';
        console.error('Errore originale:', (data as Error).message);
        console.error('Errore tradotto:', errorMessage);
        return left(new Error(errorMessage));
      }
    } catch (error) {
      const errorMessage =
        dict.auth_errors?.[(error as Error).message] ||
        dict.auth_errors?.generic_error ||
        (error as Error).message;
      console.error('Errore originale:', (error as Error).message);
      console.error('Errore tradotto:', errorMessage);
      return left(new Error(errorMessage));
    }
  };
  return taskEither(task);
};

/**
 * Utility function to make authenticated API calls with automatic token refresh
 * @param url The URL to fetch
 * @param options Fetch options
 * @param user The authenticated user
 * @param setUser Function to update the user state
 * @param auth Firebase Auth instance
 * @returns A TaskEither with the fetch response or an error
 */
export const authenticatedFetch = (
  url: string,
  options: RequestInit,
  user: AuthUser | null,
  setUser: (user: AuthUser) => void,
  auth: Auth,
): TaskEither<Error, Response> => {
  const task = async (): Promise<Either<Error, Response>> => {
    try {
      // Get a fresh token
      const tokenResult = await getTokenService(user, setUser, auth)();
      if (tokenResult._tag === 'Left') {
        return left(tokenResult.value);
      }

      const token = tokenResult.value;

      // Add the token to the request headers
      const headers = new Headers(options.headers);
      headers.set('Authorization', `Bearer ${token}`);

      // Make the request with the fresh token
      const response = await fetch(url, {
        ...options,
        headers,
      });

      // If we get a 401 Unauthorized, the token might be invalid
      // This could happen if the token was revoked on the server side
      if (response.status === 401) {
        // Try to force refresh the token
        try {
          const newToken = await auth.currentUser?.getIdToken(true);
          if (newToken && user) {
            // Update the user with the new token
            setUser({
              ...user,
              token: newToken,
              tokenCreationDate: new Date().toISOString(),
            });

            // Retry the request with the new token
            headers.set('Authorization', `Bearer ${newToken}`);
            const retryResponse = await fetch(url, {
              ...options,
              headers,
            });

            return right(retryResponse);
          }
        } catch (refreshError) {
          console.error('Error during forced token refresh:', refreshError);
          return left(new Error('Token refresh failed after 401 response'));
        }
      }

      return right(response);
    } catch (error) {
      return left(error instanceof Error ? error : new Error('An unexpected error occurred'));
    }
  };

  return taskEither(task);
};

export const oidcAuthorize = ({
  client_id,
  email,
  password,
  redirect_uri,
  state,
  token,
}: {
  email?: string;
  password?: string;
  client_id: string | null;
  redirect_uri: string | null;
  state: string | null;
  token?: string;
}): TaskEither<Error, { redirectUrl: string; valid: boolean }> => {
  const task = async (): Promise<Either<Error, { redirectUrl: string; valid: boolean }>> => {
    const dict = await getModuleDictionary('it', 'auth');
    try {
      const response = await fetch(`${process.env.NEXT_PUBLIC_HOST}/api/authorize`, {
        method: 'POST',
        body: JSON.stringify({ email, password, client_id, redirect_uri, state, token }),
        headers: { 'Content-Type': 'application/json' },
      });

      if (response && !response.ok) {
        const errorResponse = await response.json();
        const errorMessage =
          dict.auth_errors?.[errorResponse.code] ||
          dict.auth_errors?.generic_error ||
          errorResponse.message ||
          "Errore durante la chiamata all'API di oidc auth authorize";
        console.log('Errore originale:', (errorResponse as Error).message);
        console.log('Errore tradotto:', errorMessage);
        return left(new Error(errorMessage));
      }

      const { redirectUrl, valid } = await response.json();
      console.log(`OIDC response: ${redirectUrl} - isValid?${valid}`);

      return right({ redirectUrl, valid });
    } catch (error) {
      const firebaseErrorCode = (error as any).code;
      const errorMessage =
        dict.auth_errors?.[firebaseErrorCode] ||
        dict.auth_errors?.generic_error ||
        (error as Error).message ||
        'Errore';
      console.log('Errore originale:', (error as Error).message);
      console.log('Errore tradotto:', errorMessage);
      return left(new Error(errorMessage));
    }
  };
  return taskEither(task);
};

export const registerGuestUser = ({
  email,
  name,
  oobCode,
  password,
  auth,
}: {
  email: string;
  password: string;
  name: string;
  oobCode: string;
  auth: Auth;
}): TaskEither<Error, boolean> => {
  const task = async (): Promise<Either<Error, boolean>> => {
    const dict = await getModuleDictionary('it', 'auth');

    const passwordSchema = z
      .string()
      .min(8, 'Must be at least 8 characters')
      .regex(/\d/, 'Must contain at least one number');
    const result = passwordSchema.safeParse(password);

    if (!result.success) {
      return left(new Error(dict.auth_errors?.short_password));
    }
    try {
      await confirmPasswordReset(auth, oobCode, password);

      const response = await fetch(
        `${process.env.NEXT_PUBLIC_HOST}/api/register/complete-guest-registration`,
        {
          method: 'POST',
          body: JSON.stringify({
            email,
            name,
          }),
        },
      );

      if (!response.ok || response.status >= 400) {
        const errorResponse = await response.json();
        const errorMessage =
          dict.auth_errors?.[errorResponse.code] ||
          dict.auth_errors?.generic_error ||
          errorResponse.message ||
          "Errore durante la chiamata all'API di registrazione";
        console.log('Errore originale:', (errorResponse as Error).message);
        console.log('Errore tradotto:', errorMessage);
        return left(new Error(errorMessage));
      }
      console.log(`User successfully registered in backend`);
      return right(true);
    } catch (error) {
      const firebaseErrorCode = (error as any).code;
      const errorMessage =
        dict.auth_errors?.[firebaseErrorCode] ||
        dict.auth_errors?.generic_error ||
        (error as Error).message ||
        'Errore durante la registrazione.';
      console.log('Errore originale:', (error as Error).message);
      console.log('Errore tradotto:', errorMessage);
      return left(new Error(errorMessage));
    }
  };
  return taskEither(task);
};

interface InitGuestUserResponse {
  oobCode: string;
  email: string;
  stripeCustomerId: string;
  strapiUserId: string;
}

export const initGuestUser = (sessionId: string): TaskEither<Error, InitGuestUserResponse> => {
  const task = async (): Promise<Either<Error, InitGuestUserResponse>> => {
    const dict = await getModuleDictionary('it', 'auth');
    try {
      const response = await fetch(
        `${process.env.NEXT_PUBLIC_HOST}/api/register/create-guest-registration`,
        {
          method: 'POST',
          body: JSON.stringify({
            sessionId,
          }),
        },
      );

      if (!response.ok || response.status >= 400) {
        const errorResponse = await response.json();
        const errorMessage =
          dict.auth_errors?.[errorResponse.code] ||
          dict.auth_errors?.generic_error ||
          errorResponse.message ||
          "Errore durante la chiamata all'API di registrazione";
        console.log('Errore originale:', (errorResponse as Error).message);
        console.log('Errore tradotto:', errorMessage);
        return left(new Error(errorMessage));
      }
      console.log(`User successfully registered in backend`);

      const res: InitGuestUserResponse = await response.json();

      return right(res);
    } catch (error) {
      const firebaseErrorCode = (error as any).code;
      const errorMessage =
        dict.auth_errors?.[firebaseErrorCode] ||
        dict.auth_errors?.generic_error ||
        (error as Error).message ||
        'Errore durante la registrazione.';
      console.log('Errore originale:', (error as Error).message);
      console.log('Errore tradotto:', errorMessage);
      return left(new Error(errorMessage));
    }
  };
  return taskEither(task);
};
